import { useState, useEffect, useCallback } from 'react';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import {
  getInvoices,
  getInvoice,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  recordPayment,
  createInvoiceFromQuote,
  invoicePath,
  type Invoice,
  type InvoiceDetail,
  type InvoiceStatus,
  type CreateInvoiceData,
  type UpdateInvoiceData,
  type RecordPaymentData,
} from '@/lib/api/invoices';
import {
  InvoiceDetailModal,
  InvoiceModal,
  RecordPaymentModal,
} from '@/components/invoices';
import { SendInvoiceModal } from '@/components/invoices/SendInvoiceModal';
import { getQuotes } from '@/lib/api/quotes';
import { getClients } from '@/lib/api/clients';
import { getJobs } from '@/lib/api/jobs';
import { useToast } from '@/lib/hooks/useToast';
import { ToastContainer, LargeTitleHeader, SegmentedControl, PullToRefresh, SwipeableRow } from '@/components/common';
import type { SwipeAction } from '@/components/common';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { jobPath } from '@/lib/utils/navPaths';

// ── localStorage keys ─────────────────────────────────────────────────────────
const LS_FILTER = 'invoices_filter';
const LS_SORT   = 'invoices_sort';

const money = (n: number) =>
  `$${(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d: string) =>
  d ? new Date(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const STATUS_BADGE: Record<InvoiceStatus, string> = {
  draft:     'bg-gray-100 text-gray-600',
  sent:      'bg-blue-100 text-blue-700',
  viewed:    'bg-purple-100 text-purple-700',
  partial:   'bg-amber-100 text-amber-700',
  paid:      'bg-green-100 text-green-700',
  overdue:   'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
  revised:   'bg-gray-100 text-gray-500',
};

type Filter = 'all' | 'outstanding' | 'overdue' | 'paid';
type SortField = 'invoice_number' | 'client' | 'date' | 'due_date' | 'total' | 'balance';
type SortDir = 'asc' | 'desc';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all',         label: 'All' },
  { id: 'outstanding', label: 'Outstanding' },
  { id: 'overdue',     label: 'Overdue' },
  { id: 'paid',        label: 'Paid' },
];

function readLS<T>(key: string, fallback: T): T {
  try { const v = localStorage.getItem(key); return v ? (JSON.parse(v) as T) : fallback; } catch { return fallback; }
}

export const InvoicesPage = () => {
  // ?new=1 auto-open (dashboard quick actions)
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('new') === '1') {
      setShowCreate(true);
      sp.delete('new');
      window.history.replaceState(null, '', window.location.pathname + (sp.toString() ? '?' + sp.toString() : ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const navigate = useNavigate();
  const { toasts, removeToast, toast } = useToast();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<Filter>(() => readLS<Filter>(LS_FILTER, 'all'));
  const [search, setSearch] = useState('');

  // ── sort state (remembered) ────────────────────────────────────────────────
  const [sortField, setSortField] = useState<SortField>(() => readLS<{ f: SortField; d: SortDir }>(LS_SORT, { f: 'invoice_number', d: 'desc' }).f);
  const [sortDir, setSortDir]     = useState<SortDir>(() => readLS<{ f: SortField; d: SortDir }>(LS_SORT, { f: 'invoice_number', d: 'desc' }).d);

  // ── bulk select ───────────────────────────────────────────────────────────
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkSending, setBulkSending] = useState(false);

  // ── inline due-date editing ───────────────────────────────────────────────
  const [editingDueDate, setEditingDueDate] = useState<{ id: number; value: string } | null>(null);

  // Modals
  const [detailInvoice, setDetailInvoice] = useState<InvoiceDetail | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showQuotePicker, setShowQuotePicker] = useState(false);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editInvoice, setEditInvoice] = useState<(Invoice & { items?: [] }) | null>(null);
  const [confirmDeleteInvoice, setConfirmDeleteInvoice] = useState(false);

  // Clients / Jobs for create modal
  const [clients, setClients] = useState<{ id: string; name: string; company?: string }[]>([]);
  const [jobs, setJobs] = useState<{ id: number; title: string }[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number>(0);

  // persist filter + sort
  useEffect(() => { try { localStorage.setItem(LS_FILTER, JSON.stringify(filter)); } catch {} }, [filter]);
  useEffect(() => { try { localStorage.setItem(LS_SORT, JSON.stringify({ f: sortField, d: sortDir })); } catch {} }, [sortField, sortDir]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await getInvoices({ limit: 500 });
      setInvoices(result.invoices);
    } catch {
      setError('Failed to load invoices.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Promise.all([getClients(), getJobs({ limit: 500 })]).then(([c, j]) => {
      setClients(c || []);
      setJobs(j.jobs || []);
    }).catch(() => {});
  }, []);

  // The /invoices list stays the "all invoices" view, but a specific invoice resolves
  // to the unified nested destination (clients › jobs › invoices).
  const openDetail = (inv: Invoice) => {
    navigate(invoicePath(inv));
  };

  const handleCreate = async (data: CreateInvoiceData) => {
    const result = await createInvoice(data);
    toast.success(`Invoice ${result.invoiceNumber} created`);
    setShowCreate(false);
    await load();
    const detail = await getInvoice(result.id);
    setDetailInvoice(detail);
  };

  const handleUpdate = async (data: UpdateInvoiceData, invoiceId?: number) => {
    if (!invoiceId) return;
    await updateInvoice(invoiceId, data);
    toast.success('Invoice updated');
    setShowEdit(false);
    setEditInvoice(null);
    await load();
    if (detailInvoice?.id === invoiceId) {
      const updated = await getInvoice(invoiceId);
      setDetailInvoice(updated);
    }
  };

  const handleRecordPayment = async (paymentData: RecordPaymentData) => {
    if (!detailInvoice) return;
    await recordPayment(detailInvoice.id, paymentData);
    toast.success('Payment recorded');
    setShowRecordPayment(false);
    await load();
    const updated = await getInvoice(detailInvoice.id);
    setDetailInvoice(updated);
  };

  const handleSendSuccess = async (emailed: boolean) => {
    setShowSend(false);
    toast.success(emailed ? 'Invoice emailed to client' : 'Invoice marked as sent');
    await load();
    if (detailInvoice) {
      const updated = await getInvoice(detailInvoice.id);
      setDetailInvoice(updated);
    }
  };

  const handleDelete = () => {
    if (!detailInvoice) return;
    setConfirmDeleteInvoice(true);
  };

  const confirmDeleteInvoiceAction = async () => {
    if (!detailInvoice) return;
    setConfirmDeleteInvoice(false);
    await deleteInvoice(detailInvoice.id);
    toast.success('Invoice deleted');
    setDetailInvoice(null);
    await load();
  };

  const handleImportFromQuote = async (quoteId: number) => {
    const result = await createInvoiceFromQuote(quoteId);
    toast.success(`Invoice ${result.invoiceNumber} created from quote`);
    setShowQuotePicker(false);
    await load();
    const detail = await getInvoice(result.id);
    setDetailInvoice(detail);
  };

  // Filtering + search
  const filtered = invoices.filter(inv => {
    if (filter === 'outstanding') { if (inv.balance_due <= 0) return false; }
    if (filter === 'overdue')     { if (inv.status !== 'overdue') return false; }
    if (filter === 'paid')        { if (inv.status !== 'paid') return false; }
    if (search) {
      const q = search.toLowerCase();
      return (
        inv.invoice_number.toLowerCase().includes(q) ||
        (inv.client_name || '').toLowerCase().includes(q) ||
        (inv.client_company || '').toLowerCase().includes(q) ||
        (inv.job_title || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Sortable columns
  const toggleSort = (field: SortField) => {
    if (sortField === field) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortField(field); setSortDir('asc'); }
  };
  const SortIcon = ({ field }: { field: SortField }) => sortField !== field ? null : (
    <span className="material-symbols-outlined text-xs">{sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>
  );
  const visible = [...filtered].sort((a, b) => {
    let av: string | number = 0, bv: string | number = 0;
    if (sortField === 'invoice_number') { av = a.invoice_number; bv = b.invoice_number; }
    else if (sortField === 'client')    { av = (a.client_company || a.client_name || '').toLowerCase(); bv = (b.client_company || b.client_name || '').toLowerCase(); }
    else if (sortField === 'date')      { av = a.invoice_date || ''; bv = b.invoice_date || ''; }
    else if (sortField === 'due_date')  { av = a.due_date || ''; bv = b.due_date || ''; }
    else if (sortField === 'total')     { av = a.total; bv = b.total; }
    else if (sortField === 'balance')   { av = a.balance_due; bv = b.balance_due; }
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // Bulk helpers
  const allVisibleIds = visible.map(i => i.id);
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every(id => selected.has(id));
  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(allVisibleIds));
  };
  const toggleOne = (id: number) => setSelected(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });

  // Inline due-date save
  const saveDueDate = async (id: number, value: string) => {
    try {
      await updateInvoice(id, { due_date: value });
      setInvoices(prev => prev.map(i => i.id === id ? { ...i, due_date: value } : i));
      toast.success('Due date updated');
    } catch {
      toast.error('Failed to update due date');
    }
    setEditingDueDate(null);
  };

  // Bulk send invoices
  const handleBulkSend = async () => {
    const ids = Array.from(selected).filter(id => {
      const inv = invoices.find(i => i.id === id);
      return inv && (inv.status === 'draft' || inv.status === 'sent' || inv.status === 'viewed');
    });
    if (ids.length === 0) { toast.warning('No sendable invoices selected'); return; }
    setBulkSending(true);
    let sent = 0;
    for (const id of ids) {
      try { await updateInvoice(id, { status: 'sent' }); sent++; } catch {}
    }
    await load();
    setSelected(new Set());
    setBulkSending(false);
    toast.success(`${sent} invoice${sent !== 1 ? 's' : ''} marked as sent`);
  };

  // Export to CSV
  const handleExportCSV = () => {
    const rows = visible;
    const header = ['Invoice #', 'Client', 'Job', 'Invoice Date', 'Due Date', 'Total', 'Paid', 'Balance', 'Status'];
    const csvRows = [header.join(','), ...rows.map(inv => [
      inv.invoice_number,
      `"${(inv.client_company || inv.client_name || '').replace(/"/g, '""')}"`,
      `"${(inv.job_title || '').replace(/"/g, '""')}"`,
      inv.invoice_date || '',
      inv.due_date || '',
      inv.total.toFixed(2),
      inv.amount_paid.toFixed(2),
      inv.balance_due.toFixed(2),
      inv.status,
    ].join(','))];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `invoices-${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  // counts available for future use
  const _counts = {
    all:         invoices.length,
    outstanding: invoices.filter(i => i.balance_due > 0).length,
    overdue:     invoices.filter(i => i.status === 'overdue').length,
    paid:        invoices.filter(i => i.status === 'paid').length,
  };
  void _counts;

  // Summary KPIs
  const totalOutstanding = invoices.reduce((s, i) => s + (i.balance_due > 0 ? i.balance_due : 0), 0);
  const totalOverdue = invoices.filter(i => i.status === 'overdue').reduce((s, i) => s + i.balance_due, 0);
  const totalCollected = invoices.reduce((s, i) => s + (i.amount_paid || 0), 0);

  return (
    <MainLayout>
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <div className="space-y-4">
        {/* iOS Large Title Header */}
        <LargeTitleHeader
          title="Invoices"
          subtitle="Finance"
          actions={
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowQuotePicker(true)}
                className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                <span className="material-symbols-outlined text-base">upload</span>
                <span className="hidden sm:inline">From Quote</span>
              </button>
              <button
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors"
              >
                <span className="material-symbols-outlined text-base">add</span>
                <span className="hidden sm:inline">New Invoice</span>
              </button>
            </div>
          }
        >
          {/* SegmentedControl filter lives under the large title */}
          <SegmentedControl<Filter>
            options={FILTERS.map(f => ({ value: f.id, label: f.label }))}
            value={filter}
            onChange={setFilter}
            size="sm"
          />
        </LargeTitleHeader>

        {/* KPI row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { label: 'Outstanding', value: money(totalOutstanding), icon: 'account_balance_wallet', color: 'text-amber-700' },
            { label: 'Overdue',     value: money(totalOverdue),     icon: 'warning',                color: 'text-red-700' },
            { label: 'Collected',   value: money(totalCollected),   icon: 'payments',               color: 'text-green-700' },
          ].map(kpi => (
            <div key={kpi.label} className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-4">
              <span className={`material-symbols-outlined text-2xl ${kpi.color}`}>{kpi.icon}</span>
              <div>
                <p className="text-xs text-gray-500 font-medium">{kpi.label}</p>
                <p className={`text-xl font-bold ${kpi.color}`}>{kpi.value}</p>
              </div>
            </div>
          ))}
        </div>

        {error && (
          <div className="flex items-center justify-between gap-4 rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-medium text-red-700">{error}</p>
            <button onClick={load} className="text-sm font-bold text-red-700 hover:underline">Retry</button>
          </div>
        )}

        {/* Search + quick filter chips + export */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-gray-400 text-base">search</span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search invoices…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          {/* Quick filter chips */}
          {invoices.filter(i => i.status === 'overdue').length > 0 && (
            <button
              onClick={() => setFilter('overdue')}
              className={`flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-semibold transition-colors ${filter === 'overdue' ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'}`}
            >
              <span className="material-symbols-outlined text-sm">warning</span>
              {invoices.filter(i => i.status === 'overdue').length} Overdue
            </button>
          )}
          {invoices.filter(i => { const m = new Date().toISOString().slice(0,7); return i.invoice_date?.startsWith(m); }).length > 0 && (
            <button
              onClick={() => setSearch(new Date().toISOString().slice(0,7))}
              className="flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
            >
              <span className="material-symbols-outlined text-sm">calendar_today</span>
              This Month
            </button>
          )}
          <button
            onClick={handleExportCSV}
            title="Export visible invoices to CSV"
            className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-200 bg-white text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            <span className="material-symbols-outlined text-base">download</span>
            <span className="hidden sm:inline">CSV</span>
          </button>
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="flex items-center gap-3 px-4 py-2.5 bg-primary/5 border border-primary/20 rounded-xl">
            <span className="text-sm font-semibold text-primary">{selected.size} selected</span>
            <button
              onClick={handleBulkSend}
              disabled={bulkSending}
              className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 disabled:opacity-60 transition-colors"
            >
              <span className="material-symbols-outlined text-sm">send</span>
              {bulkSending ? 'Sending…' : 'Mark Sent'}
            </button>
            <button
              onClick={() => setSelected(new Set())}
              className="ml-auto text-xs text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
          </div>
        )}

        {/* List */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-sm text-gray-400">
              <span className="material-symbols-outlined animate-spin text-3xl text-gray-300 block mx-auto mb-2">progress_activity</span>
              Loading invoices…
            </div>
          ) : visible.length === 0 ? (
            <div className="p-12 text-center">
              <span className="material-symbols-outlined text-5xl text-gray-300 block mb-3">receipt_long</span>
              <p className="text-sm text-gray-500 mb-4">
                {filter === 'all' && !search ? 'No invoices yet.' : `No invoices match this filter.`}
              </p>
              {filter === 'all' && !search && (
                <button
                  onClick={() => setShowCreate(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-bold rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <span className="material-symbols-outlined text-base">add</span>
                  Create First Invoice
                </button>
              )}
            </div>
          ) : (
            <PullToRefresh onRefresh={load}>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-white shadow-sm">
                    <tr className="border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wide">
                      <th className="px-3 py-3">
                        <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
                          className="w-4 h-4 rounded accent-primary cursor-pointer" />
                      </th>
                      <th className="text-left px-3 py-3 font-semibold cursor-pointer hover:text-gray-700 select-none" onClick={() => toggleSort('invoice_number')}>
                        <span className="flex items-center gap-1">Invoice # <SortIcon field="invoice_number" /></span>
                      </th>
                      <th className="text-left px-5 py-3 font-semibold cursor-pointer hover:text-gray-700 select-none" onClick={() => toggleSort('client')}>
                        <span className="flex items-center gap-1">Client <SortIcon field="client" /></span>
                      </th>
                      <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">Job</th>
                      <th className="text-left px-5 py-3 font-semibold hidden sm:table-cell cursor-pointer hover:text-gray-700 select-none" onClick={() => toggleSort('date')}>
                        <span className="flex items-center gap-1">Date <SortIcon field="date" /></span>
                      </th>
                      <th className="text-left px-5 py-3 font-semibold hidden lg:table-cell cursor-pointer hover:text-gray-700 select-none" onClick={() => toggleSort('due_date')}>
                        <span className="flex items-center gap-1">Due Date <SortIcon field="due_date" /></span>
                      </th>
                      <th className="text-right px-5 py-3 font-semibold cursor-pointer hover:text-gray-700 select-none" onClick={() => toggleSort('total')}>
                        <span className="flex items-center justify-end gap-1">Total <SortIcon field="total" /></span>
                      </th>
                      <th className="text-right px-5 py-3 font-semibold hidden sm:table-cell">Paid</th>
                      <th className="text-right px-5 py-3 font-semibold cursor-pointer hover:text-gray-700 select-none" onClick={() => toggleSort('balance')}>
                        <span className="flex items-center justify-end gap-1">Balance <SortIcon field="balance" /></span>
                      </th>
                      <th className="text-left px-5 py-3 font-semibold">Status</th>
                      <th className="px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {visible.map(inv => (
                      <tr
                        key={inv.id}
                        className={`hover:bg-gray-50 transition-colors cursor-pointer ${selected.has(inv.id) ? 'bg-primary/5' : ''}`}
                        onClick={() => openDetail(inv)}
                      >
                        <td className="px-3 py-3.5" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={selected.has(inv.id)} onChange={() => toggleOne(inv.id)}
                            className="w-4 h-4 rounded accent-primary cursor-pointer" />
                        </td>
                        <td className="px-3 py-3.5 font-semibold text-primary whitespace-nowrap" onClick={e => e.stopPropagation()}>
                          <span
                            className="cursor-pointer hover:underline"
                            title="Click to copy"
                            onClick={() => { navigator.clipboard?.writeText(inv.invoice_number); toast.success('Copied!'); }}
                          >{inv.invoice_number}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="font-medium text-gray-900 truncate max-w-[160px]">
                            {inv.client_company || inv.client_name || '—'}
                          </p>
                          {inv.client_company && inv.client_name && (
                            <p className="text-xs text-gray-400 truncate max-w-[160px]">{inv.client_name}</p>
                          )}
                        </td>
                        <td className="px-5 py-3.5 text-gray-500 hidden md:table-cell truncate max-w-[160px]">
                          {inv.job_id ? (
                            <Link
                              to={jobPath(inv.client_id, inv.job_id)}
                              className="hover:text-primary hover:underline"
                              onClick={e => e.stopPropagation()}
                            >
                              {inv.job_title || `Job #${inv.job_id}`}
                            </Link>
                          ) : '—'}
                        </td>
                        <td className="px-5 py-3.5 text-gray-500 hidden sm:table-cell whitespace-nowrap">
                          {fmtDate(inv.invoice_date)}
                        </td>
                        <td className="px-5 py-3.5 text-gray-500 hidden lg:table-cell whitespace-nowrap" onClick={e => e.stopPropagation()}>
                          {editingDueDate?.id === inv.id ? (
                            <input
                              type="date"
                              autoFocus
                              value={editingDueDate.value}
                              onChange={e => setEditingDueDate({ id: inv.id, value: e.target.value })}
                              onBlur={e => saveDueDate(inv.id, e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') saveDueDate(inv.id, editingDueDate.value);
                                if (e.key === 'Escape') setEditingDueDate(null);
                              }}
                              className="w-36 text-xs border border-primary rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                          ) : (
                            <button
                              title="Click to edit due date"
                              onClick={() => setEditingDueDate({ id: inv.id, value: inv.due_date || '' })}
                              className="hover:text-primary hover:underline text-left"
                            >
                              {fmtDate(inv.due_date)}
                            </button>
                          )}
                        </td>
                        <td className="px-5 py-3.5 text-right font-medium text-gray-900 whitespace-nowrap">
                          {money(inv.total)}
                        </td>
                        <td className="px-5 py-3.5 text-right text-gray-500 hidden sm:table-cell whitespace-nowrap">
                          {money(inv.amount_paid)}
                        </td>
                        <td className={`px-5 py-3.5 text-right font-semibold whitespace-nowrap ${
                          inv.balance_due > 0 ? 'text-amber-700' : 'text-gray-400'
                        }`}>
                          {money(inv.balance_due)}
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${
                            STATUS_BADGE[inv.status] ?? 'bg-gray-100 text-gray-600'
                          }`}>
                            {inv.status}
                          </span>
                        </td>
                        <td className="px-5 py-3.5" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            {inv.balance_due > 0 && inv.status !== 'cancelled' && (
                              <button
                                title="Record Payment"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  await openDetail(inv);
                                  setShowRecordPayment(true);
                                }}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors"
                              >
                                <span className="material-symbols-outlined text-base leading-none">payments</span>
                              </button>
                            )}
                            {(inv.status === 'draft' || inv.status === 'sent' || inv.status === 'viewed') && (
                              <button
                                title="Send to Client"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  await openDetail(inv);
                                  setShowSend(true);
                                }}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                              >
                                <span className="material-symbols-outlined text-base leading-none">send</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile card list with SwipeableRow */}
              <div className="sm:hidden divide-y divide-gray-50">
                {visible.map(inv => {
                  const rowActions: SwipeAction[] = [
                    ...(inv.balance_due > 0 && inv.status !== 'cancelled' ? [{
                      key: 'pay',
                      label: 'Pay',
                      icon: 'payments',
                      variant: 'default' as const,
                      onClick: async () => { await openDetail(inv); setShowRecordPayment(true); },
                    }] : []),
                    ...((inv.status === 'draft' || inv.status === 'sent' || inv.status === 'viewed') ? [{
                      key: 'send',
                      label: 'Send',
                      icon: 'send',
                      variant: 'neutral' as const,
                      onClick: async () => { await openDetail(inv); setShowSend(true); },
                    }] : []),
                  ];
                  return (
                    <SwipeableRow key={inv.id} actions={rowActions}>
                      <div
                        className="px-4 py-3.5 cursor-pointer"
                        onClick={() => openDetail(inv)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-semibold text-primary text-sm">{inv.invoice_number}</p>
                            <p className="font-medium text-gray-900 text-sm truncate">{inv.client_company || inv.client_name || '—'}</p>
                            {inv.job_title && <p className="text-xs text-gray-400 truncate">{inv.job_title}</p>}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="font-semibold text-gray-900 text-sm">{money(inv.total)}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_BADGE[inv.status] ?? 'bg-gray-100 text-gray-600'}`}>
                              {inv.status}
                            </span>
                          </div>
                        </div>
                        {inv.balance_due > 0 && (
                          <p className="text-xs text-amber-700 font-medium mt-1">Balance due: {money(inv.balance_due)}</p>
                        )}
                      </div>
                    </SwipeableRow>
                  );
                })}
              </div>
            </PullToRefresh>
          )}
        </div>

        {/* Sticky totals bar */}
        {visible.length > 0 && (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 rounded-b-xl px-5 py-2.5 flex flex-wrap items-center gap-6 text-sm shadow-sm">
            <span className="text-gray-500 font-medium">{visible.length} invoice{visible.length !== 1 ? 's' : ''}</span>
            <span className="text-gray-500">Total: <span className="font-bold text-gray-900">{money(visible.reduce((s,i) => s+i.total, 0))}</span></span>
            <span className="text-gray-500">Paid: <span className="font-bold text-green-700">{money(visible.reduce((s,i) => s+(i.amount_paid||0), 0))}</span></span>
            <span className="text-gray-500">Outstanding: <span className="font-bold text-amber-700">{money(visible.reduce((s,i) => s+(i.balance_due>0?i.balance_due:0), 0))}</span></span>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {detailInvoice && (
        <InvoiceDetailModal
          isOpen={!!detailInvoice}
          onClose={() => setDetailInvoice(null)}
          invoice={detailInvoice}
          onEdit={() => {
            setEditInvoice(detailInvoice as unknown as Invoice & { items?: [] });
            setShowEdit(true);
          }}
          onSend={() => setShowSend(true)}
          onRecordPayment={() => setShowRecordPayment(true)}
          onDelete={handleDelete}
          onRefresh={(updated) => setDetailInvoice(updated)}
        />
      )}

      {/* Edit modal */}
      {showEdit && editInvoice && (
        <InvoiceModal
          isOpen={showEdit}
          onClose={() => { setShowEdit(false); setEditInvoice(null); }}
          onSave={handleUpdate}
          invoice={editInvoice}
          clientId={Number(editInvoice.client_id)}
          clientName={editInvoice.client_company || editInvoice.client_name || ''}
          jobs={jobs.map(j => ({ id: j.id, title: j.title }))}
        />
      )}

      {/* Create modal — needs a client selection step */}
      {showCreate && (
        <CreateInvoiceFlow
          clients={clients}
          jobs={jobs}
          selectedClientId={selectedClientId}
          onSelectClient={setSelectedClientId}
          onSave={handleCreate}
          onClose={() => { setShowCreate(false); setSelectedClientId(0); }}
        />
      )}

      {/* Record Payment */}
      {detailInvoice && showRecordPayment && (
        <RecordPaymentModal
          isOpen={showRecordPayment}
          onClose={() => setShowRecordPayment(false)}
          onSubmit={handleRecordPayment}
          invoice={detailInvoice}
        />
      )}

      {/* Send Invoice */}
      {detailInvoice && showSend && (
        <SendInvoiceModal
          isOpen={showSend}
          onClose={() => setShowSend(false)}
          invoice={detailInvoice}
          onSuccess={handleSendSuccess}
          isResend={detailInvoice.status === 'sent' || detailInvoice.status === 'viewed'}
        />
      )}

      {/* Quote picker */}
      {showQuotePicker && (
        <QuotePickerModal
          onSelect={handleImportFromQuote}
          onClose={() => setShowQuotePicker(false)}
        />
      )}

      {confirmDeleteInvoice && detailInvoice && (
        <ConfirmDialog
          title="Delete Invoice"
          message={`Delete invoice ${detailInvoice.invoice_number}? This cannot be undone.`}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={confirmDeleteInvoiceAction}
          onCancel={() => setConfirmDeleteInvoice(false)}
        />
      )}
    </MainLayout>
  );
};

// ── Quote picker modal ────────────────────────────────────────────────────────

const QuotePickerModal = ({
  onSelect,
  onClose,
}: {
  onSelect: (quoteId: number) => Promise<void>;
  onClose: () => void;
}) => {
  const [quotes, setQuotes] = useState<Array<{
    id: string; quote_number: string; client_name?: string; job_title?: string; total: number; status: string;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(false);

  useEffect(() => {
    getQuotes({ limit: 200, status: 'approved' })
      .then(r => setQuotes(r.quotes))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = async (id: string) => {
    setSelecting(true);
    try {
      await onSelect(parseInt(id));
    } finally {
      setSelecting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl overflow-hidden flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">Create Invoice from Approved Quote</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="py-12 text-center">
              <span className="material-symbols-outlined animate-spin text-3xl text-gray-300 block mb-3">progress_activity</span>
              <p className="text-sm text-gray-400">Loading approved quotes…</p>
            </div>
          ) : quotes.length === 0 ? (
            <div className="py-12 text-center">
              <span className="material-symbols-outlined text-5xl text-gray-300 block mb-3">request_quote</span>
              <p className="text-sm text-gray-500">No approved quotes available.</p>
              <p className="text-xs text-gray-400 mt-1">Approve a quote first, then convert it here.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {quotes.map(q => (
                <button
                  key={q.id}
                  disabled={selecting}
                  onClick={() => handleSelect(q.id)}
                  className="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-50"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-primary text-sm">{q.quote_number}</p>
                      <p className="text-sm text-gray-700 mt-0.5">{q.job_title || q.client_name || '—'}</p>
                      {q.client_name && q.job_title && (
                        <p className="text-xs text-gray-400">{q.client_name}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">${(q.total || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Approved</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="px-6 py-3 border-t border-gray-100">
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ── Inline Create Invoice flow ────────────────────────────────────────────────

interface CreateInvoiceFlowProps {
  clients: { id: string; name: string; company?: string }[];
  jobs: { id: number; title: string }[];
  selectedClientId: number;
  onSelectClient: (id: number) => void;
  onSave: (data: CreateInvoiceData) => Promise<void>;
  onClose: () => void;
}

const CreateInvoiceFlow = ({
  clients, jobs, selectedClientId, onSelectClient, onSave, onClose,
}: CreateInvoiceFlowProps) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState(new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0]);
  const [selectedJobId, setSelectedJobId] = useState<number | undefined>();
  const [taxRate, setTaxRate] = useState(0);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([{ description: '', quantity: 1, unit_price: 0 }]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClientId) { setError('Please select a client.'); return; }
    if (items.every(i => !i.description.trim())) { setError('Add at least one line item.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({
        client_id: selectedClientId,
        job_id: selectedJobId,
        invoice_date: invoiceDate,
        due_date: dueDate,
        tax_rate: taxRate,
        notes: notes || undefined,
        items: items.filter(i => i.description.trim()).map(i => ({
          description: i.description,
          quantity: i.quantity,
          unit_price: i.unit_price,
        })),
      });
    } catch {
      setError('Failed to create invoice.');
      setSaving(false);
    }
  };

  const subtotal = items.reduce((s, i) => s + i.quantity * i.unit_price, 0);
  const tax = subtotal * (taxRate / 100);
  const total = subtotal + tax;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">New Invoice</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Client <span className="text-red-500">*</span></label>
              <select
                value={selectedClientId || ''}
                onChange={e => { onSelectClient(Number(e.target.value)); setSelectedJobId(undefined); }}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/30"
                required
              >
                <option value="">Select client…</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.company || c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Job (optional)</label>
              <select
                value={selectedJobId ?? ''}
                onChange={e => setSelectedJobId(e.target.value ? Number(e.target.value) : undefined)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="">No job</option>
                {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Invoice Date</label>
              <input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Due Date</label>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Line Items</span>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Description</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-20">Qty</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-28">Unit Price</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-28">Total</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((item, idx) => (
                    <tr key={idx}>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          autoFocus={idx === items.length - 1 && idx > 0}
                          value={item.description}
                          onChange={e => {
                            const newItems = [...items];
                            newItems[idx] = { ...item, description: e.target.value };
                            setItems(newItems);
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter' && idx === items.length - 1) {
                              e.preventDefault();
                              setItems(prev => [...prev, { description: '', quantity: 1, unit_price: 0 }]);
                            }
                          }}
                          placeholder="Description"
                          className="w-full text-sm bg-transparent border-0 focus:outline-none text-gray-900 placeholder-gray-400"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={item.quantity}
                          onChange={e => {
                            const newItems = [...items];
                            newItems[idx] = { ...item, quantity: Number(e.target.value) || 0 };
                            setItems(newItems);
                          }}
                          className="w-full text-sm bg-transparent border-0 focus:outline-none text-right text-gray-900"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price}
                          onChange={e => {
                            const newItems = [...items];
                            newItems[idx] = { ...item, unit_price: Number(e.target.value) || 0 };
                            setItems(newItems);
                          }}
                          className="w-full text-sm bg-transparent border-0 focus:outline-none text-right text-gray-900"
                        />
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900 font-medium">
                        ${(item.quantity * item.unit_price).toFixed(2)}
                      </td>
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          onClick={() => setItems(items.filter((_, i) => i !== idx))}
                          disabled={items.length === 1}
                          className="text-gray-300 hover:text-red-500 disabled:opacity-30"
                        >
                          <span className="material-symbols-outlined text-base leading-none">close</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-3 py-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setItems([...items, { description: '', quantity: 1, unit_price: 0 }])}
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  <span className="material-symbols-outlined text-sm">add</span>
                  Add line (or press Enter on last row)
                </button>
              </div>
            </div>
          </div>

          {/* Tax + totals */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Tax Rate (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={taxRate}
                onChange={e => setTaxRate(Number(e.target.value) || 0)}
                className="w-24 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <div className="text-right space-y-1 text-sm">
              <div className="flex justify-between gap-8">
                <span className="text-gray-500">Subtotal</span>
                <span className="font-medium text-gray-900">${subtotal.toFixed(2)}</span>
              </div>
              {taxRate > 0 && (
                <div className="flex justify-between gap-8">
                  <span className="text-gray-500">Tax ({taxRate}%)</span>
                  <span className="font-medium text-gray-900">${tax.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between gap-8 border-t border-gray-200 pt-1">
                <span className="font-bold text-gray-900">Total</span>
                <span className="font-bold text-primary">${total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="Payment terms, special instructions…"
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </form>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button type="submit" form="" onClick={handleSubmit} disabled={saving || !selectedClientId} className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-60">
            {saving ? 'Creating…' : 'Create Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
};
